import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react/jsx-runtime";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { useTranslation } from "react-i18next";

interface InProcessClaimProps {
  isOpen: boolean;
  onClose: () => void;
  claimStatusCode?: string;
}

export default function InProcessClaim({
  isOpen,
  onClose,
  claimStatusCode,
}: InProcessClaimProps) {
  const { t } = useTranslation();
  const CLAIM_STATUS_URL = claimStatusCode
    ? `${process.env.REACT_APP_CLAIM_STATUS_URL}/${claimStatusCode}`
    : "";

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={onClose}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all space-y-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between text-gray-800 text-lg font-semibold">
                  <Dialog.Title>Claim Already in Progress</Dialog.Title>
                  <button onClick={onClose} aria-label="Close modal">
                    <IoIosCloseCircleOutline
                      size={24}
                      className="text-gray-500 hover:text-red-500 transition"
                    />
                  </button>
                </div>

                {/* Modal Message */}
                <div className="text-gray-600 text-base">
                  {t(
                    "We’ve found that a claim is already in progress for this service plan. Click the button below to check the status and contact our service team if needed."
                  )}
                </div>

                {/* Action Button */}
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      window.open(
                        CLAIM_STATUS_URL,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    disabled={!CLAIM_STATUS_URL}
                    className="bg-brand-color hover:bg-brand-hover text-white font-medium rounded-md px-4 py-2 transition disabled:bg-opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("View Claim Status")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
